// Initialize Sentry FIRST before any other imports
import { initSentry } from "./lib/sentry"

initSentry()

import * as Sentry from "@sentry/react"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { Providers } from "./lib/providers"
import { routeTree } from "./routeTree.gen"
import "./index.css"

function ErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
	// Check if this is the Privy Solana wallet connector error
	const errorMessage = error instanceof Error ? error.message : String(error)
	const isSolanaWalletError = errorMessage.includes("Unable to find source wallet connector")

	return (
		<div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
			<h1 className="font-semibold text-xl">
				{isSolanaWalletError ? "Wallet Not Connected" : "Something went wrong"}
			</h1>
			<p className="max-w-md text-slate-600">
				{isSolanaWalletError
					? "To fund with Solana, please connect a Solana wallet like Phantom first."
					: "An unexpected error occurred. Please try again."}
			</p>
			<button
				className="rounded-lg bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"
				onClick={resetError}
			>
				Try Again
			</button>
		</div>
	)
}

const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
	// Enable View Transitions API for smooth page transitions
	defaultViewTransition: true,
	// Built-in scroll restoration: preserves on back/forward, resets on link clicks
	scrollRestoration: true,
})

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router
	}
}

const rootElement = document.getElementById("root")
if (!rootElement) {
	throw new Error("Root element not found")
}

createRoot(rootElement).render(
	<StrictMode>
		<Sentry.ErrorBoundary
			fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
		>
			<Providers>
				<RouterProvider router={router} />
			</Providers>
		</Sentry.ErrorBoundary>
	</StrictMode>,
)
